import { CLAIM_COPY } from 'components/UserActions/seatClaim';

/**
 * Seat-taken wording for the table-scoped error modal, in the saloon voice the
 * rest of the felt speaks ("Sit tight, partner…", EN-10605). Two variants: a
 * walk-up joiner has nothing held for them, so the only honest next step is to
 * pick another seat; a waitlisted player keeps their place in the queue.
 */
export const SEAT_TAKEN_COPY = {
  plain:
    'Well shoot, partner. Someone beat ya to that seat. Go on and pick yourself another empty one.',
  queued:
    "Well shoot, partner. Someone beat ya to that seat. Don't fret, you're still on the waitlist and we'll holler when the next one opens up."
} as const;

/**
 * Player-facing wording for an engine seat-request rejection.
 *
 * The engine's `gotJoinGame` errors are written for logs, not players —
 * "Table seating error: Invalid Seat #1!" tells a player nothing about what
 * happened or what to do next. Every rejection is classified here into a short
 * title, a plain explanation, and the label of the single action that makes
 * sense, so the table-scoped SeatJoinErrorModal never shows raw engine text
 * for a case we recognise. Unrecognised text is still shown (minus the log
 * prefix) so a new engine message is never swallowed.
 */
export type SeatJoinErrorKind = 'seat-taken' | 'table-full' | 'already-seated' | 'unknown';

export type SeatJoinErrorCopy = {
  kind: SeatJoinErrorKind;
  title: string;
  body: string;
  action: string;
};

const LOG_PREFIX = /^\s*table seating error:\s*/i;

export const classifySeatJoinError = (message: unknown): SeatJoinErrorKind => {
  const text = String(message ?? '');
  // "Seat taken!" (action lock) and "Invalid Seat #N!" (seat filled between the
  // client's last broadcast and the engine's check) are the same event from the
  // player's point of view: somebody else got there first.
  if (/seat taken/i.test(text) || /invalid seat/i.test(text)) return 'seat-taken';
  if (/already full/i.test(text)) return 'table-full';
  if (/already seated/i.test(text)) return 'already-seated';
  return 'unknown';
};

export const describeSeatJoinError = (
  message: unknown,
  opts: { isQueued?: boolean } = {}
): SeatJoinErrorCopy => {
  const kind = classifySeatJoinError(message);
  switch (kind) {
    case 'seat-taken':
      return {
        kind,
        title: 'Seat no longer available',
        body: opts.isQueued ? SEAT_TAKEN_COPY.queued : SEAT_TAKEN_COPY.plain,
        action: 'Choose another seat'
      };
    case 'table-full':
      return {
        kind,
        title: 'Table is full',
        body: 'This table has no open seats right now. Please try another table.',
        action: 'OK'
      };
    case 'already-seated':
      return {
        kind,
        title: 'Already seated',
        body: 'You already have a seat at this table.',
        action: 'OK'
      };
    default: {
      const stripped = String(message ?? '')
        .replace(LOG_PREFIX, '')
        .trim();
      return {
        kind,
        title: "Couldn't take that seat",
        body: stripped || CLAIM_COPY.failed,
        action: 'OK'
      };
    }
  }
};
